import React, { useCallback, useMemo, useRef, useState } from "react";

type Triple = [number, number, number];

export interface RydTernaryProps {
    value?: Triple; // controlled value (a,b,c), a+b+c will be normalized to 1
    defaultValue?: Triple; // initial value for uncontrolled mode
    onChange?: (next: Triple) => void; // receives normalized triple in [0,1], sum = 1
    size?: number; // outer width in px
    padding?: number; // inner padding around triangle
    labels?: [string, string, string]; // labels for vertices [A(top), B(left), C(right)]
    dotRadius?: number;
    disabled?: boolean;
    backgroundColor?: string;
    strokeColor?: string;
    grid?: boolean; // draw simple grid lines
}

const SQRT3 = Math.sqrt(3);

function clamp01(n: number) {
    return Math.max(0, Math.min(1, n));
}

function normalizeTriple(t: Triple): Triple {
    const nonNeg: Triple = [Math.max(0, t[0]), Math.max(0, t[1]), Math.max(0, t[2])];
    const s = nonNeg[0] + nonNeg[1] + nonNeg[2];
    if (s === 0) return [1, 0, 0];
    return [nonNeg[0] / s, nonNeg[1] / s, nonNeg[2] / s];
}

type Vec2 = { x: number; y: number };

function dot(a: Vec2, b: Vec2) {
    return a.x * b.x + a.y * b.y;
}

function sub(a: Vec2, b: Vec2): Vec2 {
    return { x: a.x - b.x, y: a.y - b.y };
}

function baryToPoint(bary: Triple, A: Vec2, B: Vec2, C: Vec2): Vec2 {
    const [u, v, w] = normalizeTriple(bary);
    return {
        x: u * A.x + v * B.x + w * C.x,
        y: u * A.y + v * B.y + w * C.y,
    };
}

function pointToBary(P: Vec2, A: Vec2, B: Vec2, C: Vec2): Triple {
    // Compute barycentric coords of P in triangle ABC
    const v0 = sub(B, A);
    const v1 = sub(C, A);
    const v2 = sub(P, A);
    const d00 = dot(v0, v0);
    const d01 = dot(v0, v1);
    const d11 = dot(v1, v1);
    const d20 = dot(v2, v0);
    const d21 = dot(v2, v1);
    const denom = d00 * d11 - d01 * d01;

    if (Math.abs(denom) < 1e-9) {
        return [1, 0, 0]; // degenerate triangle safeguard
    }

    const v = (d11 * d20 - d01 * d21) / denom; // coefficient for B
    const w = (d00 * d21 - d01 * d20) / denom; // coefficient for C
    const u = 1 - v - w; // coefficient for A

    // Project outside points back into the triangle by clamping and renormalizing
    let uu = u,
        vv = v,
        ww = w;
    uu = uu < 0 ? 0 : uu;
    vv = vv < 0 ? 0 : vv;
    ww = ww < 0 ? 0 : ww;
    uu = clamp01(uu);
    vv = clamp01(vv);
    ww = clamp01(ww);

    const s = uu + vv + ww;
    if (s === 0) return [1, 0, 0];
    return [uu / s, vv / s, ww / s];
}

export default function RydTernary({
    value,
    defaultValue = [1 / 3, 1 / 3, 1 / 3],
    onChange,
    size = 260,
    padding = 18,
    labels = ["A", "B", "C"],
    dotRadius = 6,
    disabled = false,
    backgroundColor = "#f7f7f8",
    strokeColor = "#888",
    grid = true,
}: RydTernaryProps) {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [dragging, setDragging] = useState(false);

    // Un/controlled value handling
    const [internal, setInternal] = useState<Triple>(() => normalizeTriple(defaultValue));
    const current = value ? normalizeTriple(value) : internal;
    const setValue = useCallback(
        (next: Triple) => {
            const norm = normalizeTriple(next);
            onChange?.(norm);
            if (value === undefined) setInternal(norm);
        },
        [onChange, value]
    );

    // Triangle geometry (equilateral triangle centered horizontally)
    const geom = useMemo(() => {
        const side = Math.max(10, size - 2 * padding);
        const triHeight = (SQRT3 / 2) * side;

        const A: Vec2 = { x: size / 2, y: padding }; // top
        const B: Vec2 = { x: padding, y: padding + triHeight }; // left
        const C: Vec2 = { x: padding + side, y: padding + triHeight }; // right

        const width = size;
        const height = triHeight + 2 * padding;

        return { A, B, C, side, triHeight, width, height };
    }, [size, padding]);

    const dot = useMemo(() => baryToPoint(current, geom.A, geom.B, geom.C), [current, geom]);

    const updateFromEvent = useCallback(
        (e: React.PointerEvent<SVGSVGElement>) => {
            if (!svgRef.current) return;
            const rect = svgRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const next = pointToBary({ x, y }, geom.A, geom.B, geom.C);
            setValue(next);
        },
        [geom.A, geom.B, geom.C, setValue]
    );

    const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
        if (disabled) return;
        svgRef.current?.setPointerCapture(e.pointerId);
        setDragging(true);
        updateFromEvent(e);
    };

    const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        if (!dragging || disabled) return;
        updateFromEvent(e);
    };

    const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
        if (!dragging) return;
        svgRef.current?.releasePointerCapture(e.pointerId);
        setDragging(false);
    };

    const textColor = "#333";
    const gridColor = "#ddd";

    // Simple grid lines for 20% increments
    const gridLines = useMemo(() => {
        if (!grid) return [];
        const lines: Array<[Vec2, Vec2]> = [];
        const steps = [0.2, 0.4, 0.6, 0.8];
        for (const t of steps) {
            // Lines of constant A (parallel to BC): points where A = t, i.e., bary = [t, (1-t)*s, (1-t)*(1-s)]
            // Draw as segment between B<->C interpolation at weight (1 - t)
            const p1 = baryToPoint([t, 1 - t, 0], geom.A, geom.B, geom.C);
            const p2 = baryToPoint([t, 0, 1 - t], geom.A, geom.B, geom.C);
            lines.push([p1, p2]);

            // Lines of constant B (parallel to AC)
            const q1 = baryToPoint([1 - t, t, 0], geom.A, geom.B, geom.C);
            const q2 = baryToPoint([0, t, 1 - t], geom.A, geom.B, geom.C);
            lines.push([q1, q2]);

            // Lines of constant C (parallel to AB)
            const r1 = baryToPoint([1 - t, 0, t], geom.A, geom.B, geom.C);
            const r2 = baryToPoint([0, 1 - t, t], geom.A, geom.B, geom.C);
            lines.push([r1, r2]);
        }
        return lines;
    }, [grid, geom]);

    const cursor = disabled ? "not-allowed" : dragging ? "grabbing" : "grab";
    const [a, b, c] = current;

    return (
        <div style={{ display: "inline-block", userSelect: "none" }}>
            <svg
                ref={svgRef}
                width={geom.width}
                height={geom.height}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                style={{ touchAction: "none", cursor }}
                aria-label="Ternary input"
                role="application"
            >
                {/* Background */}
                <polygon
                    points={`${geom.A.x},${geom.A.y} ${geom.B.x},${geom.B.y} ${geom.C.x},${geom.C.y}`}
                    fill={backgroundColor}
                    stroke={strokeColor}
                    strokeWidth={1}
                />

                {/* Grid */}
                {gridLines.map(([p, q], i) => (
                    <line
                        key={i}
                        x1={p.x}
                        y1={p.y}
                        x2={q.x}
                        y2={q.y}
                        stroke={gridColor}
                        strokeWidth={1}
                        strokeDasharray="4 4"
                    />
                ))}

                {/* Vertex labels */}
                <text x={geom.A.x} y={geom.A.y - 6} textAnchor="middle" fontSize={12} fill={textColor}>
                    {labels[0]}
                </text>
                <text x={geom.B.x - 6} y={geom.B.y + 12} textAnchor="end" fontSize={12} fill={textColor}>
                    {labels[1]}
                </text>
                <text x={geom.C.x + 6} y={geom.C.y + 12} textAnchor="start" fontSize={12} fill={textColor}>
                    {labels[2]}
                </text>

                {/* Draggable dot */}
                <circle
                    cx={dot.x}
                    cy={dot.y}
                    r={dotRadius}
                    fill={disabled ? "#bbb" : "#1f76d2"}
                    stroke="#fff"
                    strokeWidth={2}
                    pointerEvents="none"
                />
            </svg>

            {/* Current value readout */}
            <div
                style={{
                    marginTop: 8,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    fontSize: 12,
                    color: "#444",
                    display: "flex",
                    gap: 12,
                    justifyContent: "center",
                }}
            >
                <span>
                    {labels[0]}: {a.toFixed(3)}
                </span>
                <span>
                    {labels[1]}: {b.toFixed(3)}
                </span>
                <span>
                    {labels[2]}: {c.toFixed(3)}
                </span>
            </div>
        </div>
    );
}